import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import styles from "./InvoiceProcess.module.css";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";

const AddInvoice = ({ onSave, onCancel, VendorToken }) => {
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [openLineItemModal, setOpenLineItemModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleUploadAndExtract = async () => {
    if (!invoiceFile || !VendorToken) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("vendor_token", VendorToken);
      formData.append("invoice_pdf", invoiceFile);

      const res = await axios.post(
        API_ENDPOINT.upload_vendor_invoice_document,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const apiData = res.data;

      const mappedData = {
        po_id: apiData.pi_po_number,
        invoice_no: apiData.invoice_number,
        invoice_date: apiData.invoice_date,
        invoice_amount: apiData.grand_total,
        status: apiData.status,
        invoice_token: apiData.invoice_token,
        grn_token: apiData.grn_token,
        currency: apiData.currency,
        line_items: apiData.items.map((item, index) => ({
          slNo: index + 1,
          code: item.item_code,
          description: item.description,
          hsn: item.hsn,
          qty: item.quantity,
          unitRate: item.unit_price,
          totalRate: item.total_amount,
          tax: item.tax_rate ?? "-",
          taxableAmount: item.total_amount,
        })),
      };

      setExtractedData(mappedData);
    } catch (error) {
      console.error("Invoice upload failed", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={styles.pageContainer}>
      {/* HEADER */}
      <Box className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Add Invoice</h1>
        <Button
          variant="contained"
          onClick={onCancel}
          sx={{ fontWeight: 600, fontSize: "13px" }}
        >
          Back
        </Button>
      </Box>

      {/* UPLOADER */}
      <Box className={styles.uploadBox}>
        <p className={styles.uploadTitle}>Invoice Document</p>
        <p className={styles.uploadHint}>Drag & drop PDF invoice here</p>

        <Button variant="outlined" component="label">
          Browse File
          <input
            type="file"
            hidden
            accept="application/pdf"
            onChange={(e) => setInvoiceFile(e.target.files[0])}
          />
        </Button>

        {invoiceFile && <p className={styles.fileName}>{invoiceFile.name}</p>}
      </Box>

      {/* ACTION BUTTONS */}
      <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          disabled={!invoiceFile || loading}
          onClick={handleUploadAndExtract}
          sx={{
            fontWeight: 700,
            padding: "10px 10px",
            textTransform: "none",
            fontSize: "12px",
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Upload & Extract"
          )}
        </Button>

        <Button
          variant="outlined"
          disabled={!invoiceFile || loading}
          onClick={() => {
            setInvoiceFile(null);
            setExtractedData(null);
            setOpenLineItemModal(false);
          }}
          sx={{
            fontWeight: 700,
            padding: "10px 10px",
            textTransform: "none",
            fontSize: "12px",
          }}
        >
          Clear File
        </Button>
      </Box>

      {/* EXTRACTED DATA TABLE */}
      {extractedData && (
        <Box sx={{ mt: 5 }}>
          <Box className={styles.tableHeader}>
            <h2 className={styles.sectionTitle}>Extracted Invoice Data</h2>
            <Button
              variant="contained"
              disabled={saving}
              sx={{
                fontWeight: 700,
                fontSize: "13px",
                textTransform: "none",
                minWidth: "90px",
              }}
              onClick={() => {
                setSaving(true);

                onSave({
                  ...extractedData,
                  invoice_file: invoiceFile.name,
                });

                // simulate API delay (remove later)
                setTimeout(() => {
                  setSaving(false);
                }, 1000);
              }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "PO Number",
                    "Invoice No",
                    "Invoice Date",
                    "Invoice Amount",

                    "Action",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#e0e9ee",
                        fontSize: "15px",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {extractedData.po_id}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {extractedData.invoice_no}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {extractedData.invoice_date}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#075b97",
                    }}
                  >
                    ₹{extractedData.invoice_amount}
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="outlined"
                      size="smalargell"
                      sx={{ fontWeight: 600 }}
                      onClick={() => setOpenLineItemModal(true)}
                    >
                      View Items
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* LINE ITEMS MODAL */}
      <Dialog
        open={openLineItemModal}
        onClose={() => setOpenLineItemModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "14px" }}>
          Items
        </DialogTitle>

        <DialogContent>
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Sl.No",
                    "Item Code",
                    "Description",
                    "HSN",
                    "Qty",
                    "Unit Rate",
                    "Total",
                    "Tax",
                    "Taxable Amount",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#e0e9ee",
                        fontSize: "14px",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {extractedData?.line_items.map((item) => (
                  <TableRow key={item.slNo} hover>
                    <TableCell sx={{ fontSize: "14px" }}>{item.slNo}</TableCell>
                    <TableCell sx={{ fontSize: "14px" }}>{item.code}</TableCell>
                    <TableCell sx={{ fontSize: "14px" }}>
                      {item.description}
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px" }}>{item.hsn}</TableCell>
                    <TableCell sx={{ fontSize: "14px" }}>{item.qty}</TableCell>
                    <TableCell sx={{ fontSize: "14px" }}>
                      {item.unitRate}
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px" }}>
                      ₹
                      {(
                        Number(item.unitRate) * Number(item.qty)
                      ).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px" }}>{item.tax}</TableCell>
                    <TableCell
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#075b97",
                      }}
                    >
                      ₹{Number(item.taxableAmount).toLocaleString("en-IN")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", padding: "10px" }}
        >
          <Button
            variant="contained"
            onClick={() => setOpenLineItemModal(false)}
            sx={{ fontSize: "12px", fontWeight: "bold" }}
          >
            close
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AddInvoice;
